import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { API } from '../utils/apk';

const DEFAULT_CENTER = [22.302, 88.128];
const DEFAULT_ZOOM = 15;

const deliveryIcon = L.divIcon({
  className: '',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid white;box-shadow:0 1px 6px rgba(0,0,0,.45);"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -10],
});

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const normalizeBoy = (boy) => ({
  ...boy,
  latitude: toNumber(boy.latitude),
  longitude: toNumber(boy.longitude),
});

const MapFocus = ({ selectedBoy }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedBoy?.latitude != null && selectedBoy?.longitude != null) {
      map.setView([selectedBoy.latitude, selectedBoy.longitude], 17, {
        animate: true,
      });
    }
  }, [map, selectedBoy]);

  return null;
};

const DeliveryBoyMap = () => {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [selectedBoy, setSelectedBoy] = useState(null);
  const [places, setPlaces] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [prevLocations, setPrevLocations] = useState({});

  const fetchPlaceName = async (id, lat, lng) => {
    if (lat == null || lng == null) return;

    try {
      const geocodingUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
      const response = await axios.get(geocodingUrl);
      const placeName = response?.data?.display_name || 'Unknown Location';

      setPlaces((prevPlaces) => ({ ...prevPlaces, [id]: placeName }));
    } catch (error) {
      console.error(`Error fetching place name for ID ${id}:`, error);
    }
  };

  const loadDeliveryBoys = async () => {
    const response = await axios.get(`${API}/get/all/delivery/boy`);
    return (response?.data?.data || [])
      .map(normalizeBoy)
      .filter((boy) => boy.latitude != null && boy.longitude != null);
  };

  const fetchDeliveryBoys = async () => {
    try {
      const boys = await loadDeliveryBoys();
      setDeliveryBoys(boys);

      const initialLocations = {};
      boys.forEach((boy) => {
        initialLocations[boy._id] = { lat: boy.latitude, lng: boy.longitude };
      });
      setPrevLocations(initialLocations);

      await Promise.all(
        boys.map((boy) => fetchPlaceName(boy._id, boy.latitude, boy.longitude))
      );
    } catch (error) {
      console.error('Error fetching delivery boys:', error);
    }
  };

  const fetchUpdatedLocations = async () => {
    try {
      const updatedBoys = await loadDeliveryBoys();
      setDeliveryBoys(updatedBoys);

      setPrevLocations((previousLocations) => {
        const nextLocations = { ...previousLocations };

        updatedBoys.forEach((boy) => {
          const previous = previousLocations[boy._id];
          const moved =
            !previous ||
            previous.lat !== boy.latitude ||
            previous.lng !== boy.longitude;

          if (moved || !places[boy._id]) {
            fetchPlaceName(boy._id, boy.latitude, boy.longitude);
          }

          nextLocations[boy._id] = {
            lat: boy.latitude,
            lng: boy.longitude,
          };
        });

        return nextLocations;
      });
    } catch (error) {
      console.error('Error fetching updated locations:', error);
    }
  };

  useEffect(() => {
    fetchDeliveryBoys();

    const intervalId = setInterval(fetchUpdatedLocations, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const filteredDeliveryBoys = deliveryBoys
    .filter((boy) =>
      (boy.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="flex flex-row h-screen mt-4 bg-gray-100">
      <div className="w-1/6 p-4 bg-white h-4/5 shadow-lg overflow-hidden">
        <h2 className="text-xl font-bold mb-4">Delivery Boys</h2>

        <input
          type="text"
          className="w-full p-2 mb-4 border rounded"
          placeholder="Search delivery boy..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <ul className="overflow-y-auto h-96">
          {filteredDeliveryBoys.map((boy) => (
            <li
              key={boy._id}
              className={`p-2 cursor-pointer rounded ${
                selectedBoy && selectedBoy._id === boy._id
                  ? 'bg-blue-500 text-white'
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => setSelectedBoy(boy)}
            >
              {boy.name}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-grow h-4/5 overflow-hidden">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom
          style={{ width: '100%', height: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapFocus selectedBoy={selectedBoy} />

          {deliveryBoys.map((boy) => (
            <Marker
              key={boy._id}
              position={[boy.latitude, boy.longitude]}
              icon={deliveryIcon}
              eventHandlers={{ click: () => setSelectedBoy(boy) }}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="text-lg font-semibold">{boy.name}</h3>
                  <p>Mobile: {boy.mobileNo}</p>
                  <p>Location: {places[boy._id] || 'Loading location...'}</p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default DeliveryBoyMap;
